import React, { useEffect, useState } from 'react'
import { useClient } from '../../providers/supabase'
import { useTotpEnrollment } from '../../hooks/useTotpEnrollment'
import { useChangePassword } from '../../hooks/useChangePassword'

export function OnboardingEnableTwoFactor({ onComplete }: { onComplete: () => void }) {
  const client = useClient()
  const {
    pending,
    enrolling,
    enrollError,
    verifying,
    verifyError,
    startEnrollment,
    verify,
    cancel,
  } = useTotpEnrollment()

  const [code, setCode] = useState('')
  const [showSecret, setShowSecret] = useState(false)

  // Shared with the standalone ChangePasswordForm (settings) so the two don't
  // drift. Leaving both password fields blank on submit means "keep my
  // current password" rather than an error, since setting a new one is
  // optional here.
  const {
    newPassword, setNewPassword,
    newPasswordConfirmation, setNewPasswordConfirmation,
    confirmationIsInvalid,
    submitting: savingPassword,
    error: passwordError,
    submit: submitPasswordChange,
  } = useChangePassword()

  // If the user already has a verified TOTP factor (e.g. they came back to this
  // step), skip enrollment and treat the step as complete.
  useEffect(() => {
    let cancelled = false
    client.auth.mfa.listFactors().then(({ data, error }) => {
      if (cancelled || error || !data) { return }
      const verified = (data.totp || []).find((f) => f.status === 'verified')
      if (verified) {
        onComplete()
      }
    })
    return () => { cancelled = true }
  }, [client, onComplete])

  const handleVerify = async () => {
    try {
      await verify(code)
      onComplete()
    } catch {
      // verifyError state is set by the hook; nothing more to do here.
    }
  }

  const skip = async () => {
    await cancel()
    onComplete()
  }

  // One form covers both the (optional) password change and starting 2FA
  // enrollment, so the member sees both right away instead of unlocking 2FA
  // only after a separate password-change submit — mirrors how the invite-spouse
  // fields are folded into the fillProfile step. Neither password field is
  // HTML-`required` (both optional), so there's nothing for the browser's
  // native validation to gate here — `submitPasswordChange` does that instead.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword || newPasswordConfirmation) {
      try {
        await submitPasswordChange()
      } catch {
        // passwordError is set by useChangePassword and surfaced below; stay
        // on this step so the member can retry.
        return
      }
    }

    startEnrollment()
  }

  if (!pending) {
    return <div className="row mt-4">
      <div className="col-12 col-xl-8 offset-xl-2">
        <h1>Secure your account</h1>
        <p>
          Optionally set a new password below. Then add two-factor authentication
          (2FA), which protects your account by requiring a one-time code from an
          authenticator app — most people use Google Authenticator, Authy, or
          1Password — on top of your password.
        </p>
        <p className="text-muted">
          Both are optional — you can set a password or enable 2FA later from your
          settings.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <span className="form-label">New Password</span>
            <input name="password" type="password" className="form-control"
              disabled={savingPassword || enrolling}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} />
          </div>

          <div className="mb-3">
            <span className="form-label">Confirm New Password</span>
            <input type="password" className={`form-control ${confirmationIsInvalid && 'is-invalid'}`}
              disabled={savingPassword || enrolling}
              value={newPasswordConfirmation}
              onChange={(e) => setNewPasswordConfirmation(e.target.value)} />
            {confirmationIsInvalid && <div className="invalid-feedback">
              This must match your new password
            </div>}
          </div>

          {passwordError && <p className="text-danger">
            {passwordError.message}
          </p>}

          {enrollError && <div className="alert alert-danger" role="alert">
            {enrollError.message}
          </div>}

          <button type="submit" className="btn btn-primary"
            disabled={savingPassword || enrolling}>
            {savingPassword || enrolling ? 'Setting up…' : 'Set up two-factor authentication'}
          </button>
          <button type="button" className="btn btn-link" onClick={skip}
            disabled={savingPassword || enrolling}>
            Skip for now
          </button>
        </form>
      </div>
    </div>
  }

  return <div className="row mt-4">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Scan this QR code</h1>
      <p>
        Open your authenticator app and scan the QR code below. Then enter the
        6-digit code your app generates to confirm.
      </p>

      <div className="text-center my-3">
        <img src={pending.qrCode} alt="TOTP QR code"
          style={{ maxWidth: '240px', width: '100%', background: 'white', padding: '8px' }} />
      </div>

      <div className="mb-3">
        <button type="button" className="btn btn-link p-0"
          onClick={() => setShowSecret((v) => !v)}>
          {showSecret ? "Hide setup key" : "Can't scan? Show setup key"}
        </button>
        {showSecret && <div className="mt-2">
          <code className="d-inline-block p-2 bg-light border rounded user-select-all">
            {pending.secret}
          </code>
          <small className="text-muted d-block mt-1">
            Enter this key manually in your authenticator app.
          </small>
        </div>}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleVerify() }}>
        <label htmlFor="totp-code" className="form-label">
          Verification code
        </label>
        <input type="text" id="totp-code" className="form-control"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />

        {verifyError && <p className="text-danger mt-2 mb-0">
          {verifyError.message}
        </p>}

        <div className="mt-3">
          <button type="submit" className="btn btn-primary"
            disabled={verifying || code.length !== 6}>
            {verifying ? 'Verifying…' : 'Verify and enable'}
          </button>
          <button type="button" className="btn btn-link" onClick={skip}
            disabled={verifying}>
            Skip for now
          </button>
        </div>
      </form>
    </div>
  </div>
}
